import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { InviteUseWithAccessPermission } from 'src/app/models/dtos/access-controller/invite-use-with-access-permission';
import { InviteExtraRelease } from 'src/app/models/securities-property/invite-extra-release';
import { InviteLimitSuspension } from 'src/app/models/securities-property/invite-limit-suspension';
import { VisitorService } from 'src/app/services/access-controler/visitor.service';
import { SecuritiesPropertyService } from 'src/app/services/securities-property/securities-property.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { LiberarCompraConvitesDialogComponent } from '../liberar-compra-convites-dialog/liberar-compra-convites-dialog.component';
import { InviteHistoricDialogComponent } from '../invite-historic-dialog/invite-historic-dialog.component';
import { LiberarConviteGratisDialogComponent } from '../liberar-convite-gratis-dialog/liberar-convite-gratis-dialog.component';
import { SuspenderRegrasConviteDialogComponent } from '../suspender-regras-convite-dialog/suspender-regras-convite-dialog.component';

@Component({
  selector: 'app-guest-invite-management',
  templateUrl: './guest-invite-management.component.html',
  styleUrls: ['./guest-invite-management.component.scss'],
})
export class GuestInviteManagementComponent implements OnInit {
  titleNumber: string = '';
  filterYear: number =0;
  yearOptions: SelectItem[] = [];

  /** Resultado bruto da API (não filtrar diretamente no template). */
  inviteRows: InviteUseWithAccessPermission[] = [];

  /** Metadados do título retornados pela API (após busca bem-sucedida). */
  invitesAfterRecharge: number | null = null;
  invitesNow: number | null = null;
  lastRecharge: string | null = null;

  /** Liberações extras (por dia) do título no ano filtrado. */
  extraReleases: InviteExtraRelease[] = [];

  /** Suspensões de regras de limite (por dia) do título no ano filtrado. */
  suspensions: InviteLimitSuspension[] = [];

  /** True após uma busca que retornou envelope (mesmo com lista vazia). */
  resultLoaded = false;

  /** ID do título atual na marina (após busca que encontrou o título). */
  securitiesPropertyId: number | null = null;

  /**
   * Mês (1–12) para filtrar só no front, sobre {@link inviteRows}.
   * 0 = todos os meses (evita {@code null} no p-dropdown, que quebra o visual no PrimeNG).
   */
  filterMonth = 0;
  monthOptions: SelectItem[] = [];
  private readonly liberationStatusLabelMap: Record<string, string> = {
    WAITING: 'Aguardando',
    ALLOWED: 'Liberado',
    DENIED: 'Negado',
    DENIED_BY_CUSTOMER: 'Negado pelo Cliente',
    EXPIRED: 'Expirado',
    WAITING_CUSTOMER: 'Aguardando Cliente',
    CANCELED: 'Cancelado',
  };

  constructor(
    private visitorService: VisitorService,
    private securitiesPropertyService: SecuritiesPropertyService,
    private dialog: DialogService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil
  ) {}

  ngOnInit(): void {
    const yearNow = new Date().getFullYear();
    const startYear = 2025;
    const endYear = Math.max(yearNow, startYear);
    for (let y = startYear; y <= endYear; y++) {
      this.yearOptions.push({ label: String(y), value: y });
    }
    this.filterYear = yearNow >= startYear ? yearNow : startYear;

    this.monthOptions = [
      { label: 'Todos os meses', value: 0 },
      { label: 'Janeiro', value: 1 },
      { label: 'Fevereiro', value: 2 },
      { label: 'Março', value: 3 },
      { label: 'Abril', value: 4 },
      { label: 'Maio', value: 5 },
      { label: 'Junho', value: 6 },
      { label: 'Julho', value: 7 },
      { label: 'Agosto', value: 8 },
      { label: 'Setembro', value: 9 },
      { label: 'Outubro', value: 10 },
      { label: 'Novembro', value: 11 },
      { label: 'Dezembro', value: 12 },
    ];
  }

  /**
   * Convites “grátis” no ano consumidos do saldo: sem pedido e SEM liberação extra.
   * Liberações extras são contabilizadas separadamente.
   */
  get freeInvitesYearCount(): number {
    return this.inviteRows.filter((r) => this.isFromBalanceFree(r)).length;
  }

  /**
   * Convites pagos consumidos do limite anual: com pedido e SEM liberação extra.
   * No mês selecionado ou no ano inteiro.
   */
  get paidInvitesCount(): number {
    const rows =
      this.filterMonth > 0 ? this.displayedRows : this.inviteRows;
    return rows.filter((r) => this.isFromBalancePaid(r)).length;
  }

  /** Total de convites liberados como extras no ano (soma das quantidades). */
  get extrasGrantedYearCount(): number {
    return this.extraReleases
      .filter((r) => !r.canceledAt)
      .reduce((sum, r) => sum + (r.quantity || 0), 0);
  }

  /** Convites extras já consumidos no ano (soma de consumed). */
  get extrasConsumedYearCount(): number {
    return this.extraReleases
      .filter((r) => !r.canceledAt)
      .reduce((sum, r) => sum + (r.consumed || 0), 0);
  }

  private isFromBalanceFree(r: InviteUseWithAccessPermission): boolean {
    return (
      (r.orderId === null || r.orderId === undefined) &&
      (r.extraReleaseId === null || r.extraReleaseId === undefined)
    );
  }

  private isFromBalancePaid(r: InviteUseWithAccessPermission): boolean {
    return (
      r.orderId !== null &&
      r.orderId !== undefined &&
      (r.extraReleaseId === null || r.extraReleaseId === undefined)
    );
  }

  /** Rótulo da coluna "Origem" para cada linha da tabela. */
  getOriginLabel(r: InviteUseWithAccessPermission): string {
    if (r.extraReleaseId != null) {
      return r.orderId != null ? 'Extra pago (dia)' : 'Extra grátis (dia)';
    }
    return r.orderId != null ? 'Pago' : 'Saldo grátis';
  }

  getOriginClass(r: InviteUseWithAccessPermission): string {
    if (r.extraReleaseId != null) {
      return r.orderId != null ? 'origin-pill origin-pill--extra-paid' : 'origin-pill origin-pill--extra-free';
    }
    return r.orderId != null ? 'origin-pill origin-pill--paid' : 'origin-pill origin-pill--free';
  }

  /** Linhas após filtro local por mês (data de emissão). */
  get displayedRows(): InviteUseWithAccessPermission[] {
    if (this.filterMonth == null || this.filterMonth === 0) {
      return this.inviteRows;
    }
    return this.inviteRows.filter((r) => {
      if (!r.emissionDate) {
        return false;
      }
      const d = new Date(r.emissionDate);
      if (isNaN(d.getTime())) {
        return false;
      }
      return d.getMonth() + 1 === this.filterMonth;
    });
  }

  search(): void {
    const trimmed = (this.titleNumber || '').trim();
    if (!trimmed) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Informe o número do título.'
      );
      return;
    }
    const titleNum = parseInt(trimmed, 10);
    if (isNaN(titleNum)) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Número do título inválido.'
      );
      return;
    }

    this.spinner.show();
    this.visitorService
      .findInviteUsesByTitleNumberAndYear(titleNum, this.filterYear)
      .subscribe({
        next: (data) => {
          this.inviteRows = data.inviteUses ?? [];
          this.securitiesPropertyId = data.securitiesPropertyId ?? null;
          this.invitesAfterRecharge = data.invitesAfterRecharge ?? null;
          this.invitesNow = data.invitesNow ?? null;
          this.lastRecharge = data.lastRecharge ?? null;
          this.resultLoaded = true;
          this.filterMonth = 0;
          this.loadExtraReleases();
          this.loadSuspensions();
          this.spinner.hide();
        },
        error: (error) => {
          this.spinner.hide();
          this.inviteRows = [];
          this.securitiesPropertyId = null;
          this.invitesAfterRecharge = null;
          this.extraReleases = [];
          this.suspensions = [];
          this.invitesNow = null;
          this.lastRecharge = null;
          this.resultLoaded = false;
          const ex = error?.error?.data?.exception;
          if (ex) {
            this.messageUtil.generateMessage(
              ex.type,
              ex.title,
              ex.message
            );
          } else {
            this.messageUtil.generateMessage(
              'error',
              'Erro',
              'Não foi possível carregar os convites.'
            );
          }
        },
      });
  }

  private loadExtraReleases(): void {
    if (this.securitiesPropertyId == null) {
      this.extraReleases = [];
      return;
    }
    this.securitiesPropertyService
      .findExtraReleasesByYear(this.securitiesPropertyId, this.filterYear)
      .subscribe({
        next: (rows) => {
          this.extraReleases = rows ?? [];
        },
        error: () => {
          this.extraReleases = [];
        },
      });
  }

  private loadSuspensions(): void {
    if (this.securitiesPropertyId == null) {
      this.suspensions = [];
      return;
    }
    this.securitiesPropertyService
      .findSuspensionsByYear(this.securitiesPropertyId, this.filterYear)
      .subscribe({
        next: (rows) => {
          this.suspensions = rows ?? [];
        },
        error: () => {
          this.suspensions = [];
        },
      });
  }

  openSuspenderRegrasModal(): void {
    if (this.securitiesPropertyId == null) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Busque um título válido antes de suspender as regras.'
      );
      return;
    }
    this.dialog
      .open(SuspenderRegrasConviteDialogComponent, {
        data: { securitiesPropertyId: this.securitiesPropertyId },
        header: 'Suspender regras de convite',
        width: '620px',
        height: '520px',
        modal: true,
        dismissableMask: true,
      })
      .onClose.subscribe((saved: boolean) => {
        if (saved) {
          this.loadSuspensions();
        }
      });
  }

  cancelSuspension(suspension: InviteLimitSuspension): void {
    if (!suspension || suspension.id == null) {
      return;
    }
    const msg =
      'Cancelar a suspensão de regras do dia ' +
      new Date(suspension.suspensionDate).toLocaleDateString('pt-BR') +
      '?';
    if (!window.confirm(msg)) {
      return;
    }
    this.spinner.show();
    this.securitiesPropertyService.cancelSuspension(suspension.id).subscribe({
      next: () => {
        this.spinner.hide();
        this.messageUtil.generateMessage(
          'success',
          'Sucesso',
          'Suspensão cancelada.'
        );
        this.loadSuspensions();
      },
      error: (error) => {
        this.spinner.hide();
        const ex = error?.error?.data?.exception;
        if (ex) {
          this.messageUtil.generateMessage(ex.type, ex.title, ex.message);
        } else {
          this.messageUtil.generateMessage(
            'error',
            'Erro',
            'Não foi possível cancelar a suspensão.'
          );
        }
      },
    });
  }

  cancelExtraRelease(release: InviteExtraRelease): void {
    if (!release || release.id == null) {
      return;
    }
    if ((release.consumed || 0) > 0) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Esta liberação já foi consumida e não pode ser cancelada.'
      );
      return;
    }
    const msg =
      'Cancelar a liberação extra do dia ' +
      new Date(release.releaseDate).toLocaleDateString('pt-BR') +
      ' (' +
      release.quantity +
      ' convite' +
      (release.quantity === 1 ? '' : 's') +
      ')?';
    if (!window.confirm(msg)) {
      return;
    }
    this.spinner.show();
    this.securitiesPropertyService.cancelExtraRelease(release.id).subscribe({
      next: () => {
        this.spinner.hide();
        this.messageUtil.generateMessage(
          'success',
          'Sucesso',
          'Liberação extra cancelada.'
        );
        this.loadExtraReleases();
      },
      error: (error) => {
        this.spinner.hide();
        const ex = error?.error?.data?.exception;
        if (ex) {
          this.messageUtil.generateMessage(ex.type, ex.title, ex.message);
        } else {
          this.messageUtil.generateMessage(
            'error',
            'Erro',
            'Não foi possível cancelar a liberação extra.'
          );
        }
      },
    });
  }

  getLiberationStatusLabel(status: string | null | undefined): string {
    const normalized = (status ?? '').trim().toUpperCase();
    return this.liberationStatusLabelMap[normalized] ?? (status || '—');
  }

  getLiberationStatusClass(status: string | null | undefined): string {
    const normalized = (status ?? '').trim().toUpperCase();
    switch (normalized) {
      case 'ALLOWED':
        return 'status-pill status-pill--allowed';
      case 'DENIED':
      case 'DENIED_BY_CUSTOMER':
      case 'EXPIRED':
      case 'CANCELED':
        return 'status-pill status-pill--blocked';
      case 'WAITING':
      case 'WAITING_CUSTOMER':
        return 'status-pill status-pill--waiting';
      default:
        return 'status-pill';
    }
  }

  openLiberarCompraModal(): void {
    if (this.securitiesPropertyId == null) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Busque um título válido antes de liberar a compra de convites.'
      );
      return;
    }
    this.dialog
      .open(LiberarCompraConvitesDialogComponent, {
        data: { securitiesPropertyId: this.securitiesPropertyId },
        header: 'Liberar convites extras',
        width: '620px',
        height: '600px',
        modal: true,
        dismissableMask: true,
      })
      .onClose.subscribe((saved: boolean) => {
        if (saved) {
          this.search();
        }
      });
  }

  openInviteHistoricModal(): void {
    if (this.securitiesPropertyId == null) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Busque um título válido antes de abrir o histórico.'
      );
      return;
    }
    this.dialog.open(InviteHistoricDialogComponent, {
      data: {
        securitiesPropertyId: this.securitiesPropertyId,
        year: this.filterYear,
      },
      header: 'Histórico de convites',
      width: '980px',
      height: '700px',
      modal: true,
      dismissableMask: true,
    });
  }

  openLiberarConviteGratisModal(): void {
    if (this.securitiesPropertyId == null) {
      this.messageUtil.generateMessage(
        'warning',
        'Atenção',
        'Busque um título válido antes de adicionar convites grátis.'
      );
      return;
    }
    this.dialog
      .open(LiberarConviteGratisDialogComponent, {
        data: { securitiesPropertyId: this.securitiesPropertyId },
        header: 'Adicionar convites grátis',
        width: '620px',
        height: '520px',
        modal: true,
        dismissableMask: true,
      })
      .onClose.subscribe((saved: boolean) => {
        if (saved) {
          this.search();
        }
      });
  }
}
